import React from "react";
import { StyleSheet, View, Platform, type StyleProp, type ViewStyle } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../../ThemedText";
import { ThemedButton } from "../../ThemedButton";
import { ThemedToolTip } from "../../ThemedToolTip";
import { useThemeColor } from "@/hooks/useThemeColor";
import type { MediaSize } from "../types";

export interface IMediaToolbarProps {
  visible?: boolean;
  position?: { top: number; left: number };
  mediaId?: string;
  mediaTitle?: string;
  title?: string;
  mediaType?: "image" | "video";
  currentSize?: MediaSize | string;
  currentAlign?: "left" | "center" | "right";
  onChangeSize?: (size: MediaSize) => void;
  onSizeChange?: (size: MediaSize) => void;
  onChangeAlign?: (align: "left" | "center" | "right") => void;
  onAlignChange?: (align: "left" | "center" | "right") => void;
  onViewMedia?: () => void;
  onDeleteMedia?: () => void;
  onRemove?: () => void;
  onClose?: () => void;
  isStandalonePreview?: boolean;
  style?: StyleProp<ViewStyle>;
}

const PRESET_SIZES: MediaSize[] = ["25%", "50%", "75%", "100%"];
const PRESET_ALIGNS: { label: string; value: "left" | "center" | "right"; icon: "align-left" | "align-center" | "align-right" }[] = [
  { label: "Left", value: "left", icon: "align-left" },
  { label: "Center", value: "center", icon: "align-center" },
  { label: "Right", value: "right", icon: "align-right" },
];

/**
 * MediaToolbar (formerly MediaFormatToolbar)
 *
 * Floating popup toolbar for embedded media element formatting.
 * Features:
 * - Width form-fits only as wide as the toolbar buttons (`width: "auto"`).
 * - Standardized to ThemedButton across all actions.
 * - Prevents mousedown default on web to preserve active selection.
 * - Can be positioned centered below content.
 */
export const MediaToolbar: React.FC<IMediaToolbarProps> = ({
  visible = true,
  position,
  mediaTitle,
  title,
  mediaType = "image",
  currentSize = "50%",
  currentAlign = "left",
  onChangeSize,
  onSizeChange,
  onChangeAlign,
  onAlignChange,
  onViewMedia,
  onDeleteMedia,
  onRemove,
  onClose,
  isStandalonePreview = false,
  style,
}) => {
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textColor = useThemeColor({}, "text");

  if (!visible) return null;

  const displayTitle = title || mediaTitle || "Media";
  const handleSizeChange = onSizeChange || onChangeSize;
  const handleAlignChange = onAlignChange || onChangeAlign;
  const handleRemove = onRemove || onDeleteMedia;

  const containerStyle = isStandalonePreview
    ? [
        styles.popupContainer,
        {
          backgroundColor: cardBg,
          borderColor,
          position: "relative" as const,
          top: 0,
          left: 0,
        },
        style,
      ]
    : [
        styles.popupContainer,
        {
          backgroundColor: cardBg,
          borderColor,
          position: "absolute" as const,
          zIndex: 1000,
          top: position ? Math.max(8, position.top) : 8,
          left: position ? Math.max(8, position.left) : 16,
        },
        style,
      ];

  return (
    <View
      style={containerStyle}
      testID="media-toolbar"
      {...(Platform.OS === "web"
        ? ({
            onMouseDown: (e: any) => e.preventDefault(),
          } as any)
        : {})}
    >
      <View style={styles.controlsRow}>
        <Ionicons
          name={mediaType === "video" ? "videocam-outline" : "image-outline"}
          size={14}
          color={textColor}
          style={{ marginRight: 2 }}
        />

        {/* Alignment Options */}
        <View style={styles.section}>
          {PRESET_ALIGNS.map((alignItem) => {
            const isSelected = currentAlign === alignItem.value;
            const iconName = alignItem.value === "left"
              ? "format-align-left"
              : alignItem.value === "center"
              ? "format-align-center"
              : "format-align-right";
            return (
              <ThemedToolTip key={alignItem.value} text={`Align ${alignItem.label}`}>
                <ThemedButton
                  title={alignItem.label[0]}
                  variant={isSelected ? "primary" : "minimal"}
                  style={styles.toolBtn}
                  textStyle={styles.btnText}
                  onPress={() => handleAlignChange?.(alignItem.value)}
                  accessibilityLabel={`Align ${alignItem.label}`}
                />
              </ThemedToolTip>
            );
          })}
        </View>

        <View style={[styles.divider, { backgroundColor: borderColor }]} />

        {/* Size Presets */}
        <View style={styles.section}>
          {PRESET_SIZES.map((sz) => {
            const isSelected = currentSize === sz;
            return (
              <ThemedToolTip key={sz} text={`Scale to ${sz}`}>
                <ThemedButton
                  title={sz}
                  variant={isSelected ? "primary" : "minimal"}
                  style={styles.toolBtn}
                  textStyle={styles.btnText}
                  onPress={() => handleSizeChange?.(sz)}
                  accessibilityLabel={`Size ${sz}`}
                />
              </ThemedToolTip>
            );
          })}
        </View>

        {/* View / Inspect Media */}
        {onViewMedia && (
          <>
            <View style={[styles.divider, { backgroundColor: borderColor }]} />
            <ThemedToolTip text="View Media Fullscreen">
              <ThemedButton
                title=""
                icon={<Ionicons name="eye-outline" size={13} color={textColor} />}
                variant="minimal"
                style={styles.toolBtn}
                onPress={onViewMedia}
                accessibilityLabel="Inspect Media"
              />
            </ThemedToolTip>
          </>
        )}

        {/* Remove Media */}
        {handleRemove && (
          <>
            <View style={[styles.divider, { backgroundColor: borderColor }]} />
            <ThemedToolTip text="Delete from Description">
              <ThemedButton
                title=""
                icon={<Ionicons name="trash-outline" size={13} color="#DC2626" />}
                variant="minimal"
                style={[styles.toolBtn, { backgroundColor: "#FEE2E2", borderColor: "#EF4444" }]}
                onPress={handleRemove}
                accessibilityLabel="Remove Media"
              />
            </ThemedToolTip>
          </>
        )}

        {/* Close Button */}
        {onClose && (
          <ThemedButton
            title=""
            icon={<Ionicons name="close" size={13} color={textColor} />}
            variant="minimal"
            style={styles.toolBtn}
            onPress={onClose}
            accessibilityLabel="Close media toolbar"
          />
        )}
      </View>
    </View>
  );
};

// Aliased export for backwards compatibility
export const MediaFormatToolbar = MediaToolbar;

const styles = StyleSheet.create({
  popupContainer: {
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 5,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 10,
    elevation: 8,
    width: "auto",
    alignSelf: "center",
    maxWidth: "96%",
  },
  controlsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  section: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  divider: {
    width: 1,
    height: 18,
    marginHorizontal: 3,
  },
  toolBtn: {
    minWidth: 26,
    height: 26,
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 4,
  },
  btnText: {
    fontSize: 10,
    fontWeight: "700",
  },
});
